import { useRef, useState } from "react";
import type { SaidaRota } from "../../lib/trpc.js";
import { trpc } from "../../lib/trpc.js";
import { enviarArquivos, type ProgressoUpload } from "../../lib/api.js";
import { formatarTamanho } from "../../lib/imagem.js";
import { useAuth } from "../auth/useAuth.js";
import { Pagina } from "../../components/Pagina.js";
import { Botao, Entrada, Falha, Girando, Selo, Vazio } from "../../components/ui.js";
import { Seta } from "../admin/DivulgacoesDaVivencia.js";

type Musica = SaidaRota["musicas"]["list"][number];
type Playlist = SaidaRota["playlists"]["list"][number];

/**
 * A trilha da galeria.
 *
 * Duas coisas numa tela porque uma só existe pela outra: a biblioteca, que o
 * suporte sobe e todo mundo ouve, e as playlists, que cada casa monta com ela.
 * A do sistema aparece para todos com selo; só o admin a muda.
 */
export function MusicasPage() {
  const { ehAdmin, papel, usuario } = useAuth();
  const musicas = trpc.musicas.list.useQuery();
  const playlists = trpc.playlists.list.useQuery();

  // Quem monta playlist: o admin (as do sistema) e o cliente (as suas). O
  // operador escolhe na TV, mas não monta.
  const montaPlaylist = ehAdmin || papel === "cliente";
  const podeEditar = (p: Playlist) =>
    ehAdmin || (papel === "cliente" && p.ownerUserId === usuario?.id);

  return (
    <Pagina
      titulo="Músicas"
      descricao="A trilha da galeria: o suporte sobe as músicas, e cada casa monta as suas playlists."
      acao={ehAdmin ? <SubirMusicas /> : undefined}
    >
      <div className="flex flex-col gap-8">
        <section className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="font-display text-[1.125rem] leading-tight">Playlists</h2>
            {montaPlaylist && <NovaPlaylist />}
          </div>

          {playlists.isLoading ? (
            <Girando className="mx-auto my-6 size-5 text-texto-fraco" />
          ) : playlists.isError ? (
            <Falha mensagem={playlists.error.message} />
          ) : (playlists.data ?? []).length === 0 ? (
            <p className="text-[0.875rem] text-texto-suave">
              Nenhuma playlist ainda. Na TV também dá para escolher as músicas na hora.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {playlists.data!.map((p) => (
                <li key={p.id}>
                  <CartaoPlaylist
                    playlist={p}
                    musicas={musicas.data ?? []}
                    editavel={podeEditar(p)}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="flex flex-col gap-3">
          <h2 className="font-display text-[1.125rem] leading-tight">Biblioteca</h2>
          {musicas.isLoading ? (
            <Girando className="mx-auto my-6 size-5 text-texto-fraco" />
          ) : musicas.isError ? (
            <Falha mensagem={musicas.error.message} />
          ) : (musicas.data ?? []).length === 0 ? (
            <Vazio
              titulo="Nenhuma música ainda"
              descricao={
                ehAdmin
                  ? "Suba os MP3 que vão tocar nas galerias."
                  : "O suporte ainda não subiu músicas para a galeria."
              }
            />
          ) : (
            <ul className="flex flex-col divide-y divide-borda rounded-peca border border-borda bg-superficie">
              {musicas.data!.map((m) => (
                <li key={m.id}>
                  <LinhaMusica musica={m} editavel={ehAdmin} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Pagina>
  );
}

/** O botão de subir, com o progresso logo abaixo. Só o admin chega aqui. */
function SubirMusicas() {
  const utils = trpc.useUtils();
  const entrada = useRef<HTMLInputElement>(null);
  const [progresso, setProgresso] = useState<ProgressoUpload | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(arquivos: File[]) {
    if (arquivos.length === 0) return;
    setErro(null);
    setProgresso({ enviados: 0, total: 1, percent: 0 });
    try {
      await enviarArquivos("/api/musicas/upload", { nome: "files", arquivos }, setProgresso);
      await utils.musicas.list.invalidate();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha no envio");
    } finally {
      setProgresso(null);
      if (entrada.current) entrada.current.value = "";
    }
  }

  return (
    <div className="flex flex-col items-end gap-2">
      <input
        ref={entrada}
        type="file"
        accept="audio/mpeg,.mp3"
        multiple
        hidden
        onChange={(e) => void enviar(Array.from(e.target.files ?? []))}
      />
      <Botao tom="brasa" carregando={progresso !== null} onClick={() => entrada.current?.click()}>
        {progresso ? `Enviando ${progresso.percent}%` : "Subir músicas"}
      </Botao>
      {/* Quem sobe responde por tocar em público: é o que o salão vai ouvir. */}
      <p className="max-w-[18rem] text-right text-[0.75rem] leading-snug text-texto-fraco">
        MP3 de até 30 MB. Suba só músicas que se pode tocar em público.
      </p>
      {erro && <Falha mensagem={erro} />}
    </div>
  );
}

function LinhaMusica({ musica, editavel }: { musica: Musica; editavel: boolean }) {
  const utils = trpc.useUtils();
  const [editando, setEditando] = useState(false);
  const [titulo, setTitulo] = useState(musica.titulo);
  const [confirmando, setConfirmando] = useState(false);

  const atualizar = async () => {
    await utils.musicas.list.invalidate();
    await utils.playlists.list.invalidate();
  };
  const renomear = trpc.musicas.renomear.useMutation({
    onSuccess: async () => {
      setEditando(false);
      await atualizar();
    },
  });
  const apagar = trpc.musicas.delete.useMutation({ onSuccess: atualizar });

  return (
    <div className="flex flex-col gap-2 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {editando ? (
          <form
            className="flex min-w-0 flex-1 gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              renomear.mutate({ id: musica.id, titulo });
            }}
          >
            <Entrada
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              maxLength={200}
              autoFocus
            />
            <Botao tom="quieto" carregando={renomear.isPending}>
              Salvar
            </Botao>
          </form>
        ) : (
          <p className="min-w-0 flex-1 truncate text-[0.9375rem] text-texto">{musica.titulo}</p>
        )}
        <span className="text-[0.75rem] text-texto-fraco">{formatarTamanho(musica.fileSize)}</span>
      </div>

      <audio controls preload="none" src={musica.url} className="h-9 w-full [color-scheme:dark]" />

      {editavel && (
        <div className="flex flex-wrap items-center gap-3">
          <Botao tom="fantasma" formato="nu" onClick={() => setEditando((e) => !e)}>
            {editando ? "Não renomear" : "Renomear"}
          </Botao>
          {confirmando ? (
            <>
              <span className="text-[0.8125rem] text-texto-suave">
                Apagar? Ela sai também das playlists.
              </span>
              <Botao
                tom="descartar"
                carregando={apagar.isPending}
                onClick={() => apagar.mutate({ id: musica.id })}
              >
                Apagar
              </Botao>
              <Botao tom="fantasma" formato="nu" onClick={() => setConfirmando(false)}>
                Não
              </Botao>
            </>
          ) : (
            <Botao tom="fantasma" formato="nu" onClick={() => setConfirmando(true)}>
              Apagar
            </Botao>
          )}
        </div>
      )}
      {(renomear.isError || apagar.isError) && (
        <Falha mensagem={(renomear.error ?? apagar.error)!.message} />
      )}
    </div>
  );
}

function NovaPlaylist() {
  const utils = trpc.useUtils();
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState("");
  const criar = trpc.playlists.create.useMutation({
    onSuccess: async () => {
      setNome("");
      setAberto(false);
      await utils.playlists.list.invalidate();
    },
  });

  if (!aberto) {
    return (
      <Botao tom="quieto" onClick={() => setAberto(true)}>
        Nova playlist
      </Botao>
    );
  }

  return (
    <form
      className="flex flex-wrap gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        criar.mutate({ nome });
      }}
    >
      <Entrada
        value={nome}
        onChange={(e) => setNome(e.target.value)}
        placeholder="Nome da playlist"
        maxLength={120}
        autoFocus
        className="w-56"
      />
      <Botao tom="quieto" carregando={criar.isPending} disabled={nome.trim() === ""}>
        Criar
      </Botao>
      <Botao tom="fantasma" formato="nu" onClick={() => setAberto(false)}>
        Cancelar
      </Botao>
      {criar.isError && <Falha mensagem={criar.error.message} />}
    </form>
  );
}

/**
 * Uma playlist, fechada ou aberta para montar.
 *
 * Montar é como a curadoria das divulgações: setas para subir e descer, "×"
 * para tirar, a biblioteca embaixo para acrescentar, e "Salvar" grava a ordem
 * inteira de uma vez.
 */
function CartaoPlaylist({
  playlist,
  musicas,
  editavel,
}: {
  playlist: Playlist;
  musicas: Musica[];
  editavel: boolean;
}) {
  const utils = trpc.useUtils();
  const [aberta, setAberta] = useState(false);
  const [ordem, setOrdem] = useState<number[]>(playlist.musicaIds);
  const [nome, setNome] = useState(playlist.nome);
  const [confirmando, setConfirmando] = useState(false);

  const porId = new Map(musicas.map((m) => [m.id, m]));
  const fora = musicas.filter((m) => !ordem.includes(m.id));
  const mudou =
    nome.trim() !== playlist.nome ||
    ordem.length !== playlist.musicaIds.length ||
    ordem.some((id, i) => id !== playlist.musicaIds[i]);

  const invalidar = () => utils.playlists.list.invalidate();
  const renomear = trpc.playlists.renomear.useMutation();
  const definir = trpc.playlists.definirMusicas.useMutation();
  const apagar = trpc.playlists.delete.useMutation({ onSuccess: invalidar });
  const salvando = renomear.isPending || definir.isPending;
  const erro = renomear.error ?? definir.error ?? apagar.error;

  async function salvar() {
    if (nome.trim() !== playlist.nome) {
      await renomear.mutateAsync({ id: playlist.id, nome });
    }
    await definir.mutateAsync({ id: playlist.id, musicaIds: ordem });
    await invalidar();
    setAberta(false);
  }

  function mover(de: number, para: number) {
    setOrdem((atual) => {
      const nova = [...atual];
      const [item] = nova.splice(de, 1);
      nova.splice(para, 0, item!);
      return nova;
    });
  }

  return (
    <div className="rounded-peca border border-borda bg-superficie px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-[0.9375rem] text-texto">{playlist.nome}</p>
          {playlist.ownerUserId === null && <Selo tom="quieto">do sistema</Selo>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[0.8125rem] text-texto-suave">
            {playlist.musicaIds.length} {playlist.musicaIds.length === 1 ? "música" : "músicas"}
          </span>
          {editavel && (
            <Botao
              tom="fantasma"
              formato="nu"
              onClick={() => {
                setOrdem(playlist.musicaIds);
                setNome(playlist.nome);
                setAberta((a) => !a);
              }}
            >
              {aberta ? "Fechar" : "Montar"}
            </Botao>
          )}
        </div>
      </div>

      {aberta && (
        <div className="mt-4 flex flex-col gap-4 border-t border-borda pt-4">
          <Entrada value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120} />

          {ordem.length === 0 ? (
            <p className="text-[0.875rem] text-texto-suave">
              Ainda sem músicas. Acrescente da biblioteca, logo abaixo.
            </p>
          ) : (
            <ol className="flex flex-col gap-2">
              {ordem.map((id, i) => (
                <li key={id} className="flex items-center gap-2">
                  <span className="num w-5 text-right text-[0.8125rem] text-texto-fraco">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[0.875rem] text-texto">
                    {porId.get(id)?.titulo ?? "Música apagada"}
                  </span>
                  <Seta rotulo="Subir" desabilitada={i === 0} onClick={() => mover(i, i - 1)}>
                    ↑
                  </Seta>
                  <Seta
                    rotulo="Descer"
                    desabilitada={i === ordem.length - 1}
                    onClick={() => mover(i, i + 1)}
                  >
                    ↓
                  </Seta>
                  <Seta rotulo="Tirar" onClick={() => setOrdem(ordem.filter((x) => x !== id))}>
                    ×
                  </Seta>
                </li>
              ))}
            </ol>
          )}

          {fora.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-[0.8125rem] font-medium text-texto-suave">Da biblioteca</p>
              <ul className="flex flex-col gap-1.5">
                {fora.map((m) => (
                  <li key={m.id} className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-[0.875rem] text-texto-suave">
                      {m.titulo}
                    </span>
                    <Seta
                      rotulo={`Acrescentar ${m.titulo}`}
                      onClick={() => setOrdem([...ordem, m.id])}
                    >
                      +
                    </Seta>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Botao
              tom="quieto"
              carregando={salvando}
              disabled={!mudou || nome.trim() === ""}
              onClick={() => void salvar()}
            >
              Salvar
            </Botao>
            {confirmando ? (
              <>
                <span className="text-[0.8125rem] text-texto-suave">Apagar a playlist?</span>
                <Botao
                  tom="descartar"
                  carregando={apagar.isPending}
                  onClick={() => apagar.mutate({ id: playlist.id })}
                >
                  Apagar
                </Botao>
                <Botao tom="fantasma" formato="nu" onClick={() => setConfirmando(false)}>
                  Não
                </Botao>
              </>
            ) : (
              <Botao tom="fantasma" formato="nu" onClick={() => setConfirmando(true)}>
                Apagar playlist
              </Botao>
            )}
          </div>
          {erro && <Falha mensagem={erro.message} />}
        </div>
      )}
    </div>
  );
}
